import React, { useState, useEffect, useRef, memo } from 'react';
import { createRoot } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import classNames from 'classnames/bind';

const tileserver = window.tileserver_url;

const ReactMapControls = () => <></>;

/* ReactMapControl
 * Bootstraps a new React rendering root for react-based
 * map controls and handles unmounting them when the map exits.
 * mapbox-gl calls onAdd(map_instance) and onRemove() when the
 * map is entering and exiting as needed.
 */
class ReactMapControl {
  onAdd() {
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl map-controls';
    this.root = createRoot(this._container);
    this.root.render(<ReactMapControls />);
    return this._container;
  }

  onRemove() {
    if (this.root) {
      setTimeout(() => {
        if (this.root) {
          this.root.unmount();
          this.root = null;
        }
      }, 0);
    }
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
  }
}

export function emptyGeoJSON(opts = {}) {
  const empty_json = {
    'type': 'geojson',
    'data': {
      'type': 'FeatureCollection',
      'features': []
    }
  };
  return Object.assign({}, empty_json, opts);
}

export function getFirstLayerID(map) {
  const { layers } = map.getStyle();
  let firstLayerID = undefined;
  let i = 0;
  while (i < layers.length) {
    if (layers[i].type === 'symbol') {
      firstLayerID = layers[i].id;
      break;
    }
    i++;
  }
  return firstLayerID;
}

export function addNamedImage(map, name, img, width, height) {
  if (!map) return;
  const _img = new Image(width, height);
  if (!map.hasImage(name)) {
    _img.onload = () => { map.addImage(name, _img); };
    _img.src = img;
  }
}

export function pulseLayer(map, layer_id, duration = 1000) {
  let cancelled = false;
  let start = null;
  function _pulseLayer(timestamp) {
    if (!start) start = timestamp;
    const progress = Math.abs(((timestamp - start) % duration) - (duration / 2)) / (duration / 2);
    map.setPaintProperty(layer_id, 'icon-opacity', progress);
    if (!cancelled) requestAnimationFrame(_pulseLayer);
  }
  requestAnimationFrame(_pulseLayer);
  return () => cancelled = true;
}

/* MapLocator
   expects a map reference prop and either a fitBounds or flyTo prop with valid mapbox-gl opts for the
   associated function.
 */
const flyTo_defaults = { zoom: 9 };
const fitBounds_defaults = { padding: 100, duration: 1000 };

function resolveOpts(opts) {
  if (typeof opts === 'function') opts = opts();
  if (opts) {
    return opts.then ? opts : Promise.resolve(opts);
  }
  return Promise.resolve(null);
}

export const MapLocator = ({ map, flyTo, fitBounds, children }) => {
  const onClick = () => {
    if (!map) return;
    if (flyTo) {
      resolveOpts(flyTo).then(opts => opts && map.flyTo({ ...flyTo_defaults, ...opts, essential: true }));
    } else if (fitBounds) {
      resolveOpts(fitBounds).then(opts => opts && map.fitBounds({ ...fitBounds_defaults, ...opts, essential: true }));
    }
  };

  if (!map) return null;
  return (
    <button color="link" className="fly-to-button" title="Locate on Map" onClick={onClick}>
      <i className="fa-solid fa-crosshairs" />
      {children}
    </button>
  );
};

const Map = memo(({ onMount, onUnmount, className, children, mapstate, controls, ...props }) => {
  const [mapRendered, setMapRendered] = useState(false);
  const [map, setMap] = useState(null);
  const mapContainer = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let unmounted = false;
    const rafId = window.requestAnimationFrame(() => {
      if (unmounted || mapRef.current) return;
      const _map = new mapboxgl.Map({
        center: [mapstate.center.lng, mapstate.center.lat],
        zoom: mapstate.zoom,
        container: mapContainer.current,
        style: `${tileserver}/styles/positron/style.json`,
        minZoom: 3, maxZoom: 17,
        maxBounds: [[-170.52058535864126, 17.18967805283492], [-58.386828872006475, 71.4552734486845]]
      });

      window.map = _map;
      _map.addNamedImage = addNamedImage.bind(null, _map);
      _map.getFirstLayerID = getFirstLayerID.bind(null, _map);

      _map.on('load', () => {
        if (unmounted) return;
        setMapRendered(true);
        if (controls) {
          _map.addControl(new ReactMapControl());
        }
        _map.addControl(new mapboxgl.NavigationControl());
        onMount && onMount(_map);
      });

      mapRef.current = _map;
      setMap(_map);
    });

    return () => {
      unmounted = true;
      window.cancelAnimationFrame(rafId);
      onUnmount && onUnmount(mapRef.current);
      mapRef.current && mapRef.current.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRendered) return;
    window.requestAnimationFrame(() => mapRef.current && mapRef.current.resize());
  });

  return (
    <>
      <div
        {...props}
        ref={mapContainer}
        className={classNames('map-container', 'mapboxgl-map', className, { 'is-rendered': mapRendered })}
      >
        {mapRendered && React.Children.toArray(children).map((c) => React.cloneElement(c, { map }))}
        {mapRendered && controls && controls.map((c, key) => React.cloneElement(c, { map, key }))}
      </div>
    </>
  );
});

export default Map;
